import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import { ArticleShell } from "../article";
import type { JobShape } from "../../api/contracts/hiring-jobs";
import { ApplyForm } from "./apply-form";

interface Props {
  job: JobShape;
}

function formatSalary(job: JobShape): string | null {
  if (!job.salary_min && !job.salary_max) return null;
  const c = job.salary_currency ?? "USD";
  if (job.salary_min && job.salary_max) {
    return `${c} ${job.salary_min.toLocaleString()}–${job.salary_max.toLocaleString()}`;
  }
  if (job.salary_min) return `${c} ${job.salary_min.toLocaleString()}+`;
  return `Up to ${c} ${job.salary_max!.toLocaleString()}`;
}

/**
 * The public job posting — a careers "article", rendered through the unified
 * `ArticleShell`: centered reading column, the description as the body (one
 * `MarkdownProse`), the on-this-page TOC in the right rail, and the apply form
 * as the footer slot. The location / type / salary become the metadata tags.
 */
export function PublicJobPage({ job }: Props) {
  const salary = formatSalary(job);
  const tags = [
    job.location ?? undefined,
    job.employment_type ? job.employment_type.replace(/_/g, " ") : undefined,
    salary ?? undefined,
  ].filter((t): t is string => !!t);

  return (
    <ArticleShell
      eyebrow={job.department ? `Careers · ${job.department}` : "Careers"}
      title={job.title}
      meta={tags.length > 0 ? { tags } : undefined}
      body={{
        kind: "markdown",
        md: job.description?.trim()
          ? job.description
          : "_The hiring manager has not written a description yet._",
      }}
      footer={
        <Card id="apply" className="mt-10">
          <CardHeader>
            <CardTitle className="type-title text-foreground">Apply</CardTitle>
            <p className="type-body text-muted-foreground">
              Tell us about yourself. We'll get back to you within a week.
            </p>
          </CardHeader>
          <CardContent>
            <ApplyForm jobSlug={job.slug} jobTitle={job.title} />
          </CardContent>
        </Card>
      }
    />
  );
}
